import { memo, useCallback, useState, useMemo, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Icon, useTheme } from 'hoi-poi-ui';
import moment from 'moment';
import { rrulestr } from 'rrule';

import { EntityListActions } from 'actions';
import Context from 'managers/Context';
import { AGENDA } from 'constants/Entities';
import { AGENDA_EVENT } from 'constants/Constants';
import { EntityModalActions } from 'actions';
import AgendaModel from 'models/AgendaModel';

import { formatDateToBackendUTC, getActivityDateFormat, getPreviousAndNextDate } from 'utils/dates';
import { getLiteral, getLiteralWithParameters } from 'utils/getLiteral';
import { getRoute, ensureRoute } from 'utils/routes';
import { logEvent } from 'utils/tracking';
import { getNewStrRuleWithOffset } from 'utils/rrule';
import { getBackendBoolean } from 'utils/fm';

import { subscribe } from 'lib/EventBuser';
import { ENTITY_MODAL_UPDATE } from 'lib/events';

import DashboardWidget from '../components/DashboardWidget';
import DashboardWidgetRow from '../components/DashboardWidgetRow';
import EntityDetailPopover from 'containers/components/EntityDetailPopover';
import AgendaSmallDetail from 'containers/agenda/components/AgendaSmallDetail';

const EMPTY_SVG = (
    <svg xmlns="http://www.w3.org/2000/svg" width={117} height={100} fill="none">
        <g clipPath="url(#a)">
            <path fill="#E9ECEE" d="M72.102 19.665H8.5v39.65h63.602v-39.65Z" />
            <path
                fill="#fff"
                d="M24.413 27.303H12.765v7.58h11.648v-7.58ZM38.888 27.303H27.239v7.58h11.649v-7.58ZM53.362 27.303H41.714v7.58h11.648v-7.58ZM67.837 27.303H56.188v7.58h11.649v-7.58ZM24.413 38.032H12.765v7.58h11.648v-7.58ZM38.888 38.032H27.239v7.58h11.649v-7.58ZM53.362 38.032H41.714v7.58h11.648v-7.58ZM67.837 38.032H56.188v7.58h11.649v-7.58ZM24.413 48.761H12.765v7.58h11.648v-7.58ZM38.888 48.761H27.239v7.58h11.649v-7.58ZM53.362 48.761H41.714v7.58h11.648v-7.58ZM67.837 48.761H56.188v7.58h11.649v-7.58ZM25.157 24.33c.965 0 1.748-.783 1.748-1.75a1.748 1.748 0 1 0-1.748 1.75ZM54.046 24.33a1.748 1.748 0 1 0-.002-3.497 1.748 1.748 0 0 0 .002 3.497Z"
            />
            <path
                fill="#D4D8DC"
                d="M25.857 15h-1.398v8.047h1.398V15ZM54.745 15h-1.398v8.047h1.398V15Z"
            />
            <path
                fill="#FF8C00"
                d="M64.646 31.093a2.631 2.631 0 0 1-2.259 2.602 2.624 2.624 0 0 1-2.89-1.873 2.633 2.633 0 0 1 1.444-3.13 2.624 2.624 0 0 1 3.705 2.401Z"
            />
            <path
                fill="#fff"
                d="m64.24 29.685-2.594 2.595c-.163-.252-1.07-1.426-1.07-1.426.116-.118.24-.228.371-.329l.753 1.005 2.236-2.238c.113.121.215.252.303.393Z"
            />
            <path
                fill="#FF8C00"
                d="M35.698 41.822a2.632 2.632 0 0 1-2.26 2.602 2.624 2.624 0 0 1-2.89-1.873 2.633 2.633 0 0 1 1.445-3.13 2.624 2.624 0 0 1 3.705 2.401Z"
            />
            <path
                fill="#fff"
                d="m35.29 40.414-2.592 2.595c-.163-.252-1.071-1.426-1.071-1.426a3.68 3.68 0 0 1 .371-.329l.753 1.005 2.236-2.238c.113.121.215.252.304.393Z"
            />
            <path
                fill="#FF8C00"
                d="M21.224 41.822a2.631 2.631 0 0 1-2.26 2.602 2.624 2.624 0 0 1-2.89-1.873 2.632 2.632 0 0 1 1.445-3.13 2.624 2.624 0 0 1 3.297.993c.267.421.408.91.408 1.408Z"
            />
            <path
                fill="#fff"
                d="m20.817 40.414-2.593 2.595c-.163-.252-1.071-1.426-1.071-1.426.116-.118.24-.228.371-.329l.753 1.005 2.236-2.238c.113.121.215.252.303.393Z"
            />
            <path
                fill="#FFF1CC"
                d="M68.453 73.178c-.435-3.793-2.725-7.505-6.287-8.695 1.376 3.891 1.34 8.18-.101 12.108-.561 1.513-1.34 3.14-.836 4.65.313.94 1.093 1.653 1.968 2.093.875.44 1.848.643 2.809.841l.188.157c1.557-3.52 2.693-7.362 2.259-11.154Z"
            />
            <path
                fill="#FFC766"
                d="M62.13 64.555a14.838 14.838 0 0 1 3.625 8.436 6.596 6.596 0 0 1-.09 2.033 3.836 3.836 0 0 1-.927 1.739c-.415.464-.892.89-1.19 1.446-.15.287-.238.601-.26.922-.021.321.024.642.134.94.254.754.761 1.363 1.278 1.942.574.642 1.18 1.3 1.42 2.157.028.104.187.05.158-.055-.416-1.492-1.844-2.325-2.516-3.666-.314-.625-.442-1.355-.138-2.023.267-.585.758-1.026 1.183-1.492.45-.47.783-1.042.968-1.667a6.11 6.11 0 0 0 .166-2.024 14.587 14.587 0 0 0-1.016-4.43 14.947 14.947 0 0 0-2.684-4.386c-.07-.08-.18.049-.11.128Z"
            />
            <path
                fill="#FFC766"
                d="M64.979 76.555a4.225 4.225 0 0 1-1.9-2.486c-.029-.103-.187-.049-.157.055a4.394 4.394 0 0 0 1.98 2.583c.092.054.168-.098.077-.152Z"
            />
            <path
                fill="#FDF6CB"
                d="m51.222 71.408.167.11a16.15 16.15 0 0 1 2.265 1.797 17.241 17.241 0 0 1 3.763 5.235c.365.785.67 1.6.912 2.434.334 1.153.606 2.432 1.28 3.37.068.1.143.193.224.282l6.154-.08.042-.02.246.008-.03-.133c-.005-.026-.012-.051-.018-.077-.004-.017-.008-.034-.01-.05a.231.231 0 0 0-.004-.015l-.01-.044a55.51 55.51 0 0 0-.185-.762v-.003c-.489-1.934-1.141-3.84-2.06-5.58-.028-.052-.056-.106-.086-.158-.419-.782-.9-1.526-1.44-2.225a14.046 14.046 0 0 0-.948-1.097 11.596 11.596 0 0 0-2.968-2.23c-2.198-1.154-4.75-1.574-7.114-.821-.06.019-.12.038-.18.059Z"
            />
            <path
                fill="#FFDE99"
                d="M51.235 71.487c3.059.578 5.84 2.122 7.95 4.414a6.303 6.303 0 0 1 1.134 1.653c.263.603.36 1.27.28 1.932-.061.618-.194 1.245-.107 1.864.05.316.165.617.338.882.173.266.4.49.667.658.652.436 1.426.606 2.189.745.847.153 1.729.3 2.432.828.086.064.181-.076.096-.14-1.224-.919-2.876-.701-4.218-1.344-.626-.3-1.163-.795-1.313-1.506-.13-.622.006-1.269.074-1.894a3.997 3.997 0 0 0-.205-1.901 5.841 5.841 0 0 0-1.067-1.692 14.024 14.024 0 0 0-3.455-2.86 14.687 14.687 0 0 0-4.781-1.808c-.105-.02-.118.15-.014.169Z"
            />
            <path
                fill="#FFDE99"
                d="M58.55 75.121c.338-.419.521-.943.516-1.48a2.234 2.234 0 0 0-.544-1.447c-.07-.08-.199.03-.128.11.323.37.503.848.506 1.347.004.5-.169.988-.487 1.376-.069.085.068.178.136.094ZM60.671 79.176a4.19 4.19 0 0 1-3.016-.794c-.085-.063-.181.077-.096.14.9.667 2.021.96 3.14.82.108-.014.079-.18-.028-.166ZM54.525 72.47c-.098.19-.24.352-.415.474a1.277 1.277 0 0 1-.582.22c-.107.013-.078.178.028.166.23-.027.45-.112.644-.245.192-.133.35-.312.461-.52a.09.09 0 0 0 .013-.063.085.085 0 0 0-.033-.054.082.082 0 0 0-.062-.013.085.085 0 0 0-.054.036Z"
            />
            <path
                fill="#FFC766"
                d="M65.719 71.97c.515-.133.97-.446 1.285-.884.314-.438.468-.972.432-1.507-.008-.108-.174-.097-.166.011a2.15 2.15 0 0 1-1.602 2.22c-.104.027-.053.187.05.16ZM64.13 67.363c-.189.093-.397.138-.606.13a1.228 1.228 0 0 1-.589-.176c-.09-.056-.166.096-.076.151.197.118.42.185.651.194.23.01.461-.038.672-.14a.089.089 0 0 0 .053-.107.082.082 0 0 0-.104-.052Z"
            />
            <path
                fill="#7D8A96"
                d="M108.239 84.4H8.761a.306.306 0 0 1-.185-.058.18.18 0 0 1-.076-.141.18.18 0 0 1 .076-.142.306.306 0 0 1 .185-.059h99.478c.069 0 .136.021.185.059a.18.18 0 0 1 .076.142.18.18 0 0 1-.076.141.307.307 0 0 1-.185.059Z"
            />
            <path
                fill="#FFC5AC"
                d="M80.154 61.426a1.009 1.009 0 0 0 .365-.773 1 1 0 0 0-.365-.773l-.757-13.195-2.16.484 1.428 12.886a1.007 1.007 0 0 0 .13 1.341 1.03 1.03 0 0 0 1.36.03ZM88.927 63.145a1.01 1.01 0 0 1-.393-.76 1 1 0 0 1 .336-.786l.274-13.213 2.177.406-.956 12.93a1.008 1.008 0 0 1-.08 1.344 1.03 1.03 0 0 1-1.358.079ZM90.078 82.783h1.695l.484-3.916h-2.18l.001 3.916Z"
            />
            <path
                fill="#37526C"
                d="M89.646 82.235h3.338c.564 0 1.105.222 1.504.617s.623.93.623 1.489v.069h-5.465v-2.175Z"
            />
            <path fill="#FFC5AC" d="M79.501 82.783h1.695l.484-6.474H79.5v6.474Z" />
            <path
                fill="#37526C"
                d="M79.07 82.235h3.337c.565 0 1.106.222 1.505.617.398.395.622.93.623 1.489v.069h-5.466v-2.175Z"
            />
            <path
                fill="#FFC5AC"
                d="M86.33 40.992c1.531 0 2.773-1.229 2.773-2.745 0-1.517-1.242-2.746-2.773-2.746-1.532 0-2.773 1.23-2.773 2.746s1.241 2.745 2.772 2.745Z"
            />
            <path
                fill="#98D5F1"
                d="M89.854 44.583s1.232.102 1.335.712c.103.61 0 2.44 0 2.44s.822 2.441 0 3.051c-.822.61-2.978.712-2.978.712l1.643-6.915Z"
            />
            <path
                fill="#37526C"
                d="m79.379 58.414-1.13 1.83s.41 8.034.513 8.238c.082.119.118.263.103.406v1.119l.308 9.763s-.822.407.206.712c1.026.305 2.362.407 2.567-.61.205-1.017.215-9.49.205-9.763l.103-.407 1.643-6 3.8 8.339 2.26 9.356s3.42.391 2.803-.93c-.616-1.323-2.085-11.172-2.085-11.172s-.308-1.017-.308-1.22c0-.204-.102-.305-.102-.509 0-.203-.103-.813-.103-.813l-.719-6.407-10.064-1.932Z"
            />
            <path
                fill="#98D5F1"
                d="M80.405 42.854s-1.232-.102-1.54.915c-.308 1.017-2.876 4.882-2.362 5.288.513.407 4.416 1.831 4.518 1.73.103-.102-.616-7.933-.616-7.933Z"
            />
            <path
                fill="#37526C"
                d="M89.558 36.496c-.116-.43-.28-.858-.614-1.154a1.923 1.923 0 0 0-1.226-.478 3.834 3.834 0 0 0-3.433-.193.625.625 0 0 0-.273-.276l-.099.847-.382-.285-.019.502-.35-.299c-.025.592.472 1.105 1.035 1.307.562.201 1.179.164 1.776.136.296-.014.632-.009.83.207.21.227.17.574.175.88.008.431.454.22.678.59a.833.833 0 0 1 .174.423.984.984 0 0 1-.15.392c-.165.338-.493 1.387-.337 1.729l.87-.482s1.909-1.77 1.346-3.846Z"
            />
            <path
                fill="#98D5F1"
                d="M89.032 50.481s2.054-5.186 1.951-5.593c-.102-.407-2.665-2.076-2.665-2.076l-.284-.035a.462.462 0 0 0 .028-.112l.045-.398a.465.465 0 0 0-.102-.344.474.474 0 0 0-.317-.173l-3.257-.353a.477.477 0 0 0-.347.1.468.468 0 0 0-.175.314l-.038.345c-.172-.042-.18-.059-.18-.059s-2.875.452-3.594.655c-.719.204-.103 7.933-.103 7.933s-.513 7.118-.821 7.322c-.308.203 0 .102-.308.915-.041.107.526-.492.77-.356 1.602.895 8.1 2.987 9.705 2.898.513-5.302-.514-7.017-.514-7.017l.206-3.966Z"
            />
        </g>
        <defs>
            <clipPath id="a">
                <path fill="#fff" d="M8.5 15h100v70H8.5z" />
            </clipPath>
        </defs>
    </svg>
);

const NextEventsDashboardWidget = memo(() => {
    const dispatch = useDispatch();
    const theme = useTheme();

    const [isDetailVisible, setIsDetailVisible] = useState(false);
    const popoverCallbacksRef = useRef({});
    const agendaPermission = useSelector(
        (state) => state?.config?.permission?.crud_permission?.[AGENDA.permission],
    );
    const nylasConfig = useSelector((state) => state.config?.nylas);
    const syncCalendarWithNylas = useSelector(
        (state) => state.config?.userData?.syncCalendarWithNylas || false,
    );
    const idUser = useSelector((state) => state?.config?.userData?.idUsuario);
    const idTimezone = useSelector((state) => state?.config?.userData?.idTimeZone);
    const userName = useSelector(
        (state) => `${state?.config?.userData?.nombre} ${state?.config?.userData?.apellidos}`,
    );

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState();
    const [list, setList] = useState([]);

    const getList = useCallback(() => {
        setLoading(true);
        setError(false);

        let startDate = moment();
        const endDate = startDate.clone();
        endDate.endOf('day');
        endDate.add(1, 'years');

        let filters = {
            fini: {
                id: 'fini',
                value: formatDateToBackendUTC(startDate.toDate()),
            },
            ffin: {
                id: 'ffin',
                value: formatDateToBackendUTC(endDate.toDate()),
            },
            taskFlag: {
                id: 'taskFlag',
                value: '0',
            },
            completed: {
                id: 'completed',
                value: '0',
            },
            useUTCDates: {
                id: 'useUTCDates',
                value: true,
            },
            idusuario: {
                value: [idUser],
                id: 'idusuario',
            },
            newAttendees: {
                id: 'newAttendees',
                value: true,
            },
        };

        Context.domainManager.getEntityList(
            AGENDA.entity,
            0,
            50,
            filters,
            '',
            '',
            '',
            false,
            (result) => {
                setLoading(false);

                const events = result.reduce((arr, item) => {
                    let initDateString = item.Fini;
                    let initDateFormat = 'YYYY-MM-DD';
                    if (item.Hini) {
                        initDateString = initDateString + ' ' + item.Hini;
                        initDateFormat = initDateFormat + ' ' + 'HH:mm';
                    }
                    const initDate = moment.utc(initDateString, initDateFormat);
                    item.momentDate = initDate;

                    item = AgendaModel.standardToCrud(item);

                    if (!item.g_recurrence || item.g_recurrence === '') {
                        arr.push(item);
                    } else {
                        let g_recurrence =
                            item.g_recurrence?.replace('RRULE:RRULE:FREQ', 'FREQ') ||
                            item.g_recurrence;
                        g_recurrence = g_recurrence?.replace('RRULE:FREQ', 'FREQ');
                        const newGRecurrence = getNewStrRuleWithOffset(g_recurrence);
                        let rule = rrulestr(newGRecurrence, {
                            dtstart: initDate.toDate(),
                        });

                        if (startDate && endDate) {
                            const dates = rule.between(startDate.toDate(), endDate.toDate());
                            for (let i = 0; i < dates.length; i++) {
                                const momentDate = moment(dates[i]);
                                if (momentDate.isAfter(moment())) {
                                    arr.push({
                                        ...item,
                                        momentDate,
                                        Fini: momentDate.format('YYYY-MM-DD'),
                                    });
                                }
                            }
                        }
                    }
                    return arr;
                }, []);

                events.sort((a, b) => {
                    if (a.momentDate.isBefore(b.momentDate)) {
                        return -1;
                    } else if (a.momentDate.isAfter(b.momentDate)) {
                        return 1;
                    } else {
                        return 0;
                    }
                });

                // Not first because upcoming event widget
                setList(events.slice(1, 100));
            },
            (error) => {
                console.error(error);
                setLoading(false);
                setError(true);
            },
        );
    }, [idUser]);

    useEffect(() => {
        dispatch(EntityListActions.setUseLazyLoad(AGENDA, true));
        return subscribe(`${ENTITY_MODAL_UPDATE}--${AGENDA.entity}`, () => getList());
    }, [dispatch, getList]);

    useEffect(() => {
        getList();
    }, [getList]);

    const onViewMore = useCallback(() => {
        logEvent({
            event: 'dashboard',
            submodule: AGENDA.trueName,
            functionality: 'list',
        });
        window.open(getRoute(AGENDA.route), '_blank');
    }, []);

    const onAdd = useCallback(() => {
        const defaultRange = getPreviousAndNextDate(60);
        const hini = defaultRange.previous;
        const fini = new Date(hini);

        dispatch(
            EntityModalActions.init({
                entity: AGENDA,
                data: {
                    user: {
                        label: userName,
                        value: idUser,
                    },
                    fini,
                    hini,
                    idTimezone,
                    isCompletado: '0',
                    isTarea: '0',
                },
                labels: {
                    title: getLiteral('title_create_event'),
                    success: getLiteral('succes_entitycreatedsuccessfully'),
                    error: getLiteral('label_failed_insert_activity_salesforce'),
                },
                crudTab: AGENDA_EVENT,
                hideDelete: true,
            }),
        );

        logEvent({
            event: 'dashboard',
            submodule: AGENDA.trueName,
            functionality: 'create',
        });
    }, [dispatch, idTimezone, idUser, userName]);

    const onPopupVisibleChange = useCallback((isVisible) => {
        setIsDetailVisible(isVisible);
        if (popoverCallbacksRef?.current?.onPopupVisibleChange) {
            popoverCallbacksRef?.current?.onPopupVisibleChange(isVisible);
        }
    }, []);

    const onDeleteEvent = useCallback(
        (item) => {
            return new Promise((resolve, reject) => {
                const id = item.Id;
                Context.domainManager.deleteEntity(
                    AGENDA.entity,
                    id,
                    () => {
                        getList();
                        resolve();
                    },
                    (error) => {
                        console.error(error);
                        reject();
                    },
                );
            });
        },
        [getList],
    );

    const onEditEvent = useCallback(
        (item) => {
            if (!item || item?.g_recurrence) return;
            const canEditItem = getBackendBoolean(item.CanEdit);
            if (!agendaPermission?.update || !(canEditItem && !item.isReadOnly)) {
                return;
            }

            dispatch(
                EntityModalActions.init({
                    entity: AGENDA,
                    id: item.Id,
                    labels: {
                        title: getLiteral('title_edit_appointment'),
                        success: getLiteral('succes_entityupdatedsuccessfully'),
                        error: getLiteral('error_generalerror'),
                        deleteModalTitle: getLiteral('title_delete_event'),
                        successDelete: getLiteral('succes_entitydeletedsuccessfully'),
                    },
                    hideDelete: !agendaPermission?.delete,
                    disableConfirm: !agendaPermission?.update,
                    crudTab: AGENDA_EVENT,
                }),
            );

            logEvent({
                event: 'dashboard',
                submodule: AGENDA.trueName,
                functionality: 'detailView',
            });
        },
        [agendaPermission?.delete, agendaPermission?.update, dispatch],
    );

    const onSeeDayRoute = useCallback(() => {
        logEvent({
            event: 'dashboard',
            submodule: AGENDA.trueName,
            functionality: 'route',
        });
    }, []);

    const emptyActions = useMemo(() => {
        let actions = [];
        if (agendaPermission?.create)
            actions.push(
                <Button type="secondary" size="small" onClick={onAdd}>
                    {getLiteral('action_add_event')}
                </Button>,
            );

        const nylasStatus = nylasConfig?.account?.status || 0;
        if (syncCalendarWithNylas && nylasStatus <= 0) {
            actions.push(
                <Button
                    type="secondary"
                    size="small"
                    onClick={() => ensureRoute('/settings/nylas')}
                >
                    {getLiteral('action_sync_cal')}
                </Button>,
            );
        }

        return actions;
    }, [agendaPermission?.create, onAdd, nylasConfig?.account?.status, syncCalendarWithNylas]);

    const rowList = useMemo(() => {
        return list?.map((item) => {
            const canEditItem = getBackendBoolean(item.CanEdit);
            const canDeleteItem = getBackendBoolean(item.CanDelete);

            const isEventEditable =
                !item.g_recurrence && agendaPermission?.update && canEditItem && !item.isReadOnly;

            const isEventDeletable =
                !item.g_recurrence &&
                agendaPermission?.delete &&
                canEditItem &&
                canDeleteItem &&
                !item.isReadOnly;

            let popoverCustomProps = {};
            if (isEventEditable) {
                popoverCustomProps = {
                    onEdit: () => onEditEvent(item),
                    titleDelete: getLiteral('title_delete_event'),
                    subtitleDelete: getLiteralWithParameters('confirm_delete', [
                        `"${item.Asunto}"`,
                    ]),
                };
            } else {
                let labelCantEdit = '';

                if (!agendaPermission.update || !canEditItem || item.isReadOnly) {
                    labelCantEdit = getLiteral('label_calendar_no_edit_no_permission');
                }
                if (item.g_recurrence) {
                    labelCantEdit = getLiteral('label_calendar_no_edit_recurrent');
                }

                popoverCustomProps = { labelCantEdit };
            }

            if (isEventDeletable) {
                popoverCustomProps.onDelete = () => onDeleteEvent(item);
            }

            return (
                <EntityDetailPopover
                    {...popoverCustomProps}
                    content={
                        <AgendaSmallDetail
                            data={item}
                            popoverCallbacks={popoverCallbacksRef.current}
                            isDetailVisible={isDetailVisible}
                        />
                    }
                    onPopupVisibleChange={onPopupVisibleChange}
                    entity={AGENDA}
                    entityId={item?.Id}
                >
                    <DashboardWidgetRow
                        item={item}
                        //onClick={() => onClick(i)}
                        leftColumn={
                            <Icon name="event" size="large" color={theme.colors.neutral600} />
                        }
                        title={item.Asunto || '-'}
                        subtitle={getActivityDateFormat(
                            moment.utc(`${item.Fini} ${item.Hini}`, 'YYYY-MM-DD HH:mm').local(),
                        )}
                    />
                </EntityDetailPopover>
            );
        });
    }, [
        agendaPermission?.delete,
        agendaPermission?.update,
        isDetailVisible,
        list,
        onDeleteEvent,
        onEditEvent,
        onPopupVisibleChange,
        theme.colors.neutral600,
    ]);

    let showSeeDayRoute = useMemo(() => list.find((item) => item.isGeoLocated), [list]);
    showSeeDayRoute = false;

    return (
        <DashboardWidget
            title={getLiteral('label_next_events')}
            // Actions
            onViewMore={onViewMore}
            onAdd={agendaPermission?.create ? onAdd : undefined}
            addTooltip={getLiteral('action_add_event')}
            // State
            isLoading={loading}
            isEmpty={list?.length === 0 && !error}
            isError={error}
            // Empty view
            emptyTitle={getLiteral('label_empty_widget_events')}
            emptySubtitle={getLiteral('label_empty_widget_events_desc')}
            emptyImage={EMPTY_SVG}
            emptyActions={emptyActions}
        >
            {rowList}
            {showSeeDayRoute && (
                <Button type="secondary" icon="route" onClick={onSeeDayRoute}>
                    {getLiteral('label_see_day_route')}
                </Button>
            )}
        </DashboardWidget>
    );
});

export default NextEventsDashboardWidget;
